import type { Ref } from 'react';
import { Music4, Trophy } from 'lucide-react';

interface LandingTvMockProps {
  /** Refs opcionais para as camadas de parallax controladas pelo GSAP ScrollTrigger no Hero. */
  glowRef?: Ref<HTMLDivElement>;
  placarRef?: Ref<HTMLDivElement>;
  musicaRef?: Ref<HTMLDivElement>;
  celularRef?: Ref<HTMLDivElement>;
}

/**
 * Mock decorativo da TV do salão — device com arquitetura "double-bezel"
 * (casca externa + núcleo interno) exibindo placar ao vivo e card de música.
 * Puramente visual; `aria-hidden`.
 */
export function LandingTvMock({ glowRef, placarRef, musicaRef, celularRef }: LandingTvMockProps) {
  return (
    <div className="relative mx-auto w-full max-w-2xl overflow-x-clip" aria-hidden="true">
      {/* Glow ambiente atrás do device */}
      <div ref={glowRef} className="pointer-events-none absolute -inset-8 -z-10">
        <div className="absolute left-1/2 top-1/2 h-64 w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500/20 blur-3xl" />
      </div>

      {/* Casca externa (bezel) */}
      <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-2 shadow-2xl shadow-black/50 [box-shadow:inset_0_1px_1px_rgba(255,255,255,0.08)]">
        {/* Núcleo interno (tela) */}
        <div className="relative aspect-video overflow-hidden rounded-[calc(2rem-0.5rem)] bg-[#0c0c12] [box-shadow:inset_0_1px_1px_rgba(255,255,255,0.12)]">
          {/* Conteúdo de fundo da "TV" */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 via-[#0c0c12] to-[#0c0c12]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(249,115,22,0.12),transparent_55%)]" />

          {/* Placar ao vivo (canto superior direito) */}
          <div
            ref={placarRef}
            className="absolute right-2 top-2 flex max-w-[calc(100%-1rem)] flex-wrap items-center justify-end gap-1.5 rounded-2xl border border-white/10 bg-black/40 px-2 py-1.5 backdrop-blur-sm sm:right-3 sm:top-3 sm:gap-3 sm:px-3.5 sm:py-2"
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400">
              <span className="h-1.5 w-1.5 animate-pulse-slow rounded-full bg-red-500" />
              Ao vivo
            </span>
            <div className="flex items-center gap-2 text-sm font-black text-white">
              <span>CAM</span>
              <span className="rounded-md bg-orange-500/90 px-1.5 text-white">2 — 1</span>
              <span>FLA</span>
            </div>
          </div>

          {/* Card "tocando agora" (canto inferior esquerdo) */}
          <div
            ref={musicaRef}
            className="absolute bottom-2 left-2 flex max-w-[calc(100%-4.75rem)] items-center gap-2 rounded-2xl border border-white/10 bg-black/40 px-2 py-2 backdrop-blur-sm sm:bottom-3 sm:left-3 sm:max-w-[70%] sm:gap-3 sm:px-3 sm:py-2.5"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 text-white">
              <Music4 className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold leading-tight text-white">Tocando agora</p>
              <p className="truncate text-xs leading-tight text-white/50">Pedido da mesa 12 · Jukebox</p>
            </div>
            {/* Equalizer */}
            <div className="ml-1 flex items-end gap-0.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1 animate-pulse-slow rounded-full bg-orange-400"
                  style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>

          {/* Marca d'água central */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Trophy className="h-16 w-16 text-white/[0.06]" />
          </div>
        </div>
      </div>

      {/* Base/suporte da TV */}
      <div className="mx-auto mt-2 h-1.5 w-24 rounded-full bg-white/10" />

      <div
        ref={celularRef}
        className="absolute -bottom-4 right-0 w-16 sm:-bottom-6 sm:right-4 sm:w-28"
      >
        <div className="rounded-[1.35rem] border border-white/15 bg-[#0a0a0f] p-1 shadow-xl shadow-black/40">
          <div className="aspect-[9/16] overflow-hidden rounded-[1.1rem] bg-[#0f0f17] px-2 py-3">
            <p className="text-[8px] font-semibold text-white">Jukebox</p>
            <p className="mt-1 text-[7px] text-white/40">Mesa 12 · pedir música</p>
            <div className="mt-2 h-1.5 rounded-full bg-gradient-to-r from-orange-500 to-rose-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
